import { useState, useEffect, useCallback, useRef } from "react";
import { getTransactions } from "../../api/finances/transactionsAPI";
import { mapTransactionToRow } from "../../components/ui/popup/finances/utils/helpers";

const DEFAULT_TRANSACTION_FILTERS = {};

export function useTransactions(initialFilters = DEFAULT_TRANSACTION_FILTERS) {
  const [transactions, setTransactions] = useState([]);
  const [latestTransactions, setLatestTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 0,
    totalPages: 0,
  });
  const requestIdRef = useRef(0);

  const loadTransactions = useCallback(
    async (nextFilters = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setIsLoading(true);
      setError("");
      setTransactions([]);
      setLatestTransactions([]);

      try {
        const data = await getTransactions({
          ...initialFilters,
          ...nextFilters,
        });

        if (requestIdRef.current !== requestId) {
          return;
        }

        const results = Array.isArray(data) ? data : data?.results || [];
        const mappedTransactions = results.map(mapTransactionToRow);

        setTransactions(mappedTransactions);
        setLatestTransactions(mappedTransactions.slice(0, 6));
        setPagination({
          count: data?.count ?? results.length,
          page: data?.page ?? 1,
          pageSize: data?.page_size ?? results.length,
          totalPages: data?.total_pages ?? (results.length ? 1 : 0),
        });
      } catch (error) {
        if (requestIdRef.current === requestId) {
          setError("We could not load your transactions. Please try again.");
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    },
    [initialFilters],
  );

  const updateTransactionInState = useCallback((updatedTransaction) => {
    setTransactions((currentTransactions) =>
      currentTransactions.map((transaction) =>
        transaction.uuid === updatedTransaction.uuid
          ? { ...transaction, ...updatedTransaction }
          : transaction,
      ),
    );
    setLatestTransactions((currentTransactions) =>
      currentTransactions.map((transaction) =>
        transaction.uuid === updatedTransaction.uuid
          ? { ...transaction, ...updatedTransaction }
          : transaction,
      ),
    );
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  return {
    transactions,
    latestTransactions,
    isLoading,
    isInitialLoading: isLoading,
    isHydrating: false,
    error,
    pagination,
    loadTransactions,
    updateTransactionInState,
  };
}
